import React, { useState } from "react";
import axios from "axios";
import { ToastContainer } from "react-toastify";
import { Badge, Breadcrumbs, Button, Copy01, PageHeader, Tabs } from "@opendate-app/setlist";

import { BroadcastEditProvider, useBroadcastEditContext } from "../BroadcastEditContainer";
import { audienceSummary } from "../Composer/InboxPreview";
import { senderLabel } from "../Composer/smsMessage";
import { toastError } from "../../../../shared/toastHelper";
import ReportOverview from "./ReportOverview";
import ReportEmail from "./ReportEmail";
import ReportMessage from "./ReportMessage";
import ReportRecipients from "./ReportRecipients";
import ReportDetails from "./ReportDetails";
import CountBadge from "../../../shared/components/CountBadge";
import { listHref } from "../../shared/listUrlState";

const REPORT_TABS = ["overview", "recipients", "details"];

const initialTab = (contentTab) => {
  const requested = new URLSearchParams(window.location.search).get("tab");
  if (requested === "email" || requested === "message") return contentTab;

  return REPORT_TABS.includes(requested) ? requested : "overview";
};

const BroadcastReport = ({ conversionSummary, authorship }) => {
  const { csrfToken, team, broadcast, sentAt, formatDate, overviewMetrics, isEmailTemplate, twilioMessagingServices } = useBroadcastEditContext();
  const [heatmapOn, setHeatmapOn] = useState(true);
  const [duplicating, setDuplicating] = useState(false);

  const sentCount = Number(overviewMetrics.sent_count).toLocaleString();
  const contentTab = isEmailTemplate ? "email" : "message";
  const [tab, setTab] = useState(() => initialTab(contentTab));
  const sender = twilioMessagingServices?.find((service) => service.id === broadcast.twilio_template?.twilio_messaging_service_id);
  const sentFrom = !isEmailTemplate && sender ? ` from ${senderLabel(sender)}` : "";

  const changeTab = (next) => {
    setTab(next);
    const url = new URL(window.location.href);
    url.searchParams.set("tab", next);
    window.history.replaceState(null, "", url);
  };

  const duplicate = () => {
    if (duplicating) return;

    setDuplicating(true);
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios.post(`/teams/${team.id}/broadcasts/${broadcast.id}/duplicate`)
      .then(({ data }) => { window.location.href = `/teams/${team.id}/broadcasts/${data.id}/edit`; })
      .catch(() => {
        setDuplicating(false);
        toastError("Could not duplicate the broadcast, please try again");
      });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20, color: "var(--sl-text-primary)" }}>
      <PageHeader
        breadcrumbs={(
          <Breadcrumbs items={[
            { label: "Broadcasts", href: listHref(`/teams/${team.id}/broadcasts`) },
            { label: broadcast.name },
          ]} />
        )}
        title={(
          <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
            {broadcast.name}
            {!isEmailTemplate && <Badge size="sm" color="gray">SMS</Badge>}
            <Badge size="sm" color="success" dot>Sent</Badge>
          </span>
        )}
        supportingText={`Sent ${formatDate(sentAt)}${sentFrom} to ${sentCount} fans · ${audienceSummary(broadcast, true)}`}
        actions={<Button variant="secondary" icon={Copy01} disabled={duplicating} onClick={duplicate}>Duplicate</Button>}
      />

      <Tabs
        variant="underline"
        value={tab}
        onChange={changeTab}
        items={[
          { value: "overview", label: "Overview" },
          isEmailTemplate ? { value: "email", label: "Email" } : { value: "message", label: "Message" },
          { value: "recipients", label: <>Recipients <CountBadge count={overviewMetrics.sent_count} color="gray" /></> },
          { value: "details", label: "Details" },
        ]}
      />

      {tab === "overview" && (
        <ReportOverview
          conversionSummary={conversionSummary}
          onViewHeatmap={() => { setHeatmapOn(true); changeTab(contentTab); }}
        />
      )}
      {tab === "email" && <ReportEmail heatmapOn={heatmapOn} setHeatmapOn={setHeatmapOn} onDuplicate={duplicate} />}
      {tab === "message" && <ReportMessage onDuplicate={duplicate} />}
      {tab === "recipients" && <ReportRecipients />}
      {tab === "details" && <ReportDetails authorship={authorship} />}
    </div>
  );
};

const BroadcastReportContainer = (props) => (
  <BroadcastEditProvider {...props} readOnly>
    <ToastContainer icon={false} />
    <BroadcastReport conversionSummary={props.conversionSummary} authorship={props.authorship} />
  </BroadcastEditProvider>
);

export default BroadcastReportContainer;
